import { useState } from 'react';
import { trackEvent } from '../lib/analytics';

// Newsletter signups POST to the AIOS forms endpoint — one call saves the
// subscriber and sends the welcome email.
const AIOS_ENDPOINT = 'https://app.brandctl.com/api/forms/newsletter';

/**
 * Compact newsletter signup mounted inside the Footer. Same AIOS backend as
 * the larger NewsletterInlineSignup (one POST to /api/forms/newsletter) but
 * visually tuned to fit a single line under the footer's nav row instead of a
 * full banner card.
 *
 * Honeypot: a hidden `<input>` with autocomplete="off" + tabIndex={-1}
 * + display:none. Real visitors never see it. Bots that fill every
 * form field will populate it, and the submit handler silently skips
 * the API call but still shows the user a "subscribed" state so bots
 * can't probe for whether the honeypot blocked them.
 */
export const FooterNewsletter = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || submitted) return;
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Enter a valid email.');
      return;
    }

    // Honeypot tripwire. Real humans don't fill hidden fields; bots
    // that auto-fill every input do. We still set submitted=true so
    // the bot doesn't learn it was blocked.
    if (honeypot.trim().length > 0) {
      setSubmitted(true);
      return;
    }

    setBusy(true);
    setError('');
    try {
      const payload = {
        email: trimmed,
        name: name.trim(),
        source: 'Footer Newsletter',
        consent_blog: 'yes',
        _hp: honeypot,
        _source: typeof window !== 'undefined' ? window.location.href : '',
      };
      const res = await fetch(AIOS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || result.ok === false) {
        throw new Error(
          result.error === 'rate_limited'
            ? 'That is a few requests in a row. Give it a minute, or email hello@brandctl.com.'
            : result.error || 'Something went wrong. Try again.',
        );
      }
      trackEvent('newsletter_signup', { source: 'footer' });
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full border-b border-white/10 pb-4 md:pb-5 mb-4 md:mb-5">
      <div className="max-w-[1250px] mx-auto flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-6">
        <div className="text-center md:text-left">
          <p className="font-sans font-bold tracking-[0.2em] text-[#FDC2A1] text-[10px] md:text-[11px] uppercase">
            Field Notes Newsletter
          </p>
          <p className="font-serif text-white/80 text-sm md:text-base mt-1">
            Get the next teardown in your inbox.
          </p>
        </div>

        {!submitted && (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:min-w-[480px]"
          >
            <label htmlFor="footer-newsletter-name" className="sr-only">
              Your name
            </label>
            <input
              id="footer-newsletter-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              placeholder="Your name"
              className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-white placeholder:text-white/35 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] transition-colors text-sm"
            />
            <label htmlFor="footer-newsletter-email" className="sr-only">
              Email address
            </label>
            <input
              id="footer-newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="name@yourcompany.com"
              className="flex-1 min-w-0 bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-white placeholder:text-white/35 focus:outline-none focus:border-white/40 focus:bg-white/[0.07] transition-colors text-sm"
            />
            {/* Honeypot — hidden from humans by display:none + aria-hidden +
                tabIndex=-1 + autoComplete=off. Bots that fill every input
                will trip this; we silently skip the API call on submit. */}
            <input
              type="text"
              name="company_url"
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              style={{ display: 'none' }}
            />
            <button
              type="submit"
              disabled={busy}
              className="shrink-0 px-5 py-2.5 bg-white text-black font-sans font-bold text-[11px] uppercase tracking-[0.2em] rounded-full hover:bg-white/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
        )}

        {submitted && (
          <p className="font-serif italic text-white text-sm md:text-base text-center md:text-right">
            Thanks, you&rsquo;re in.
          </p>
        )}

        {error && !submitted && (
          <p className="text-[11px] uppercase tracking-[0.18em] text-red-400/85 font-bold text-center md:text-right">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};
